import type { AxiosResponse } from "axios";
import { z } from "zod";

// Not `z.uuid()`: Postgres's uuid type takes any 8-4-4-4-12 hex id, not only
// RFC 9562 ones, so a stricter check would turn real records away.
export function isRecordId(id: string | undefined): id is string {
  return z.guid().safeParse(id).success;
}

/**
 * Treats a 400 to a read of one record as not-found: an id the server rejects
 * as malformed names no record the user can open.
 */
export function isNotFoundStatus(status: number | undefined) {
  return status === 400 || status === 404;
}

/**
 * Throws a failed read to the error boundary unless it is answered elsewhere:
 * sign-in for a lapsed session (401), the access-reason prompt (451), or a
 * not-found (`isNotFoundStatus`), which the caller must route itself. A read
 * in flight or answered with a 200 is left alone; a read of one record has no
 * other success to give, so any other 2xx is thrown too, whatever its body.
 * Call it before testing whether the record has loaded, not only when it is
 * missing, or such a body renders as the record.
 *
 * A 200 still held from an earlier read deliberately outranks a failed
 * reload's error: the page keeps the record it shows rather than unmounting
 * it, and any work not yet saved.
 */
export function throwUnansweredReadFailure(
  read: { error: Error | null; response: AxiosResponse | undefined },
  record: string,
) {
  const status = read.response?.status ?? 0;
  if (status === 200) {
    return;
  }
  if (read.error) {
    throw read.error;
  }
  if (status && !isNotFoundStatus(status) && ![401, 451].includes(status)) {
    throw new Error(`Unable to load the ${record} (status ${String(status)})`);
  }
}
